import React, { useState, useEffect, lazy, Suspense } from 'react';
import './styles.css';
import ComponentLoader from './components/ComponentLoader';
import preloadComponents from './utils/preloadComponents';
import { GRADUATION_REQUIREMENT_TYPES } from './constants/graduation';
import useAppData from './hooks/useAppData';
import { 
  groupPostsByGrade, 
  getPostTagFeedbacks,
  isPostGraded,
  getPostComments,
  getFilteredPosts
} from './viewUtils';
import {
  handleAddTagFeedback,
  handleAddComment,
  handleAddTag,
  handleDeleteTag,
  handleUpdateTranscriptSettings,
  handleUpdateTagFeedback,
  handleDeleteTagFeedback,
  handleGenerateShareableTranscript,
  handleRevokeShareableTranscript,
  handleGetStudentShareableTranscripts
} from './dataHandlers';
import { useUIState } from './useUIState';
import { CompetencySummary } from './CompetencySummary';
import { SkillAveragesGrid } from './SkillAveragesGrid';
import PublicTranscriptView from './PublicTranscriptView';
import { LoginForm, AuthLoading, useAuth, authFunctions } from './AuthComponents';
import { NotificationDropdown, createNotificationClickHandler, useNotificationSystem } from './NotificationComponents';
import { usePostCreation, postCreationFunctions } from './PostCreationComponents';
import { useTimeLogging, timeLoggingUtils } from './TimeLoggingComponents';
import { useBookLogging, useBookListManagement } from './BookLoggingComponents';
import { useCertificateManagement, CertificateTeacherDisplay, CertificateParentApprovalList } from './CertificateComponents';
import { ProfileEditModal, UserInfoHeader, useProfileManagement } from './ProfileComponents';
import { useClassManagement } from './ClassManagementComponents';
import { useGraduationRequirements } from './GraduationRequirementsComponents';
import { useMentorManagement, useMentorRequests } from './MentorComponents';
import { useLorManagement } from './LorComponents';
import { useProjectProposalManagement } from './ProjectManagementComponents';
import { useTranscriptRequestManagement } from './TranscriptRequestComponents';
import { useResourceManagement } from './ResourceComponents';
import { useWordOfDayManagement } from './WordOfDayComponents';
import { usePostEditing, postActionHandlers } from './PostComponents';

// Lazy load heavy components that aren't needed on initial render
// PDF Generation is loaded dynamically in the download handler (not via lazy/Suspense)

// Chart components (only needed in specific views) - includes Chart.js bundle
const StudentScoreMatrix = lazy(() => import('./StudentScoreMatrix'));

// View-specific components - lazy load by feature
const PostCreationForm = lazy(() => import('./PostCreationComponents').then(m => ({ default: m.PostCreationForm })));
const PostItem = lazy(() => import('./PostComponents').then(m => ({ default: m.PostItem })));

const TimeLogCreationForm = lazy(() => import('./TimeLoggingComponents').then(m => ({ default: m.TimeLogCreationForm })));
const TimeLogDisplay = lazy(() => import('./TimeLoggingComponents').then(m => ({ default: m.TimeLogDisplay })));

const BookLogCreationForm = lazy(() => import('./BookLoggingComponents').then(m => ({ default: m.BookLogCreationForm })));
const BookLogDisplay = lazy(() => import('./BookLoggingComponents').then(m => ({ default: m.BookLogDisplay })));
const BookListManagementViewLazy = lazy(() => import('./BookLoggingComponents').then(m => ({ default: m.BookListManagementView })));
const BookListProgressViewLazy = lazy(() => import('./BookLoggingComponents').then(m => ({ default: m.BookListProgressView })));
const BookListProgressOverviewLazy = lazy(() => import('./BookLoggingComponents').then(m => ({ default: m.BookListProgressOverview })));

const CertificateCreationForm = lazy(() => import('./CertificateComponents').then(m => ({ default: m.CertificateCreationForm })));
const CertificateDisplay = lazy(() => import('./CertificateComponents').then(m => ({ default: m.CertificateDisplay })));

const ClassTypeManagement = lazy(() => import('./ClassManagementComponents').then(m => ({ default: m.ClassTypeManagement })));
const FlexibleClassAssignmentForm = lazy(() => import('./ClassManagementComponents').then(m => ({ default: m.FlexibleClassAssignmentForm })));
const StudentFocusedAssignment = lazy(() => import('./ClassManagementComponents').then(m => ({ default: m.StudentFocusedAssignment })));
const ClassFocusedAssignment = lazy(() => import('./ClassManagementComponents').then(m => ({ default: m.ClassFocusedAssignment })));
const ClassEditingSection = lazy(() => import('./ClassManagementComponents').then(m => ({ default: m.ClassEditingSection })));
const DeleteClassEntirely = lazy(() => import('./ClassManagementComponents').then(m => ({ default: m.DeleteClassEntirely })));
const ClassProposalForm = lazy(() => import('./ClassManagementComponents').then(m => ({ default: m.ClassProposalForm })));
const ClassProposalManagement = lazy(() => import('./ClassManagementComponents').then(m => ({ default: m.ClassProposalManagement })));

const GraduationRequirementsDisplay = lazy(() => import('./GraduationRequirementsComponents').then(m => ({ default: m.GraduationRequirementsDisplay })));

const MentorCreationForm = lazy(() => import('./MentorComponents').then(m => ({ default: m.MentorCreationForm })));
const MentorDisplay = lazy(() => import('./MentorComponents').then(m => ({ default: m.MentorDisplay })));
const LorRequestForm = lazy(() => import('./LorComponents').then(m => ({ default: m.LorRequestForm })));
const LorRequestsDisplay = lazy(() => import('./LorComponents').then(m => ({ default: m.LorRequestsDisplay })));
const LorManagementView = lazy(() => import('./LorComponents').then(m => ({ default: m.LorManagementView })));
const TranscriptRequestForm = lazy(() => import('./TranscriptRequestComponents').then(m => ({ default: m.TranscriptRequestForm })));
const TranscriptRequestsDisplay = lazy(() => import('./TranscriptRequestComponents').then(m => ({ default: m.TranscriptRequestsDisplay })));
const TeacherTranscriptRequestsDisplay = lazy(() => import('./TranscriptRequestComponents').then(m => ({ default: m.TeacherTranscriptRequestsDisplay })));
const ResourcesLandingPage = lazy(() => import('./ResourceComponents').then(m => ({ default: m.ResourcesLandingPage })));
const ResourceManagementView = lazy(() => import('./ResourceComponents').then(m => ({ default: m.ResourceManagementView })));
const WordOfDayManagementViewLazy = lazy(() => import('./WordOfDayComponents').then(m => ({ default: m.WordOfDayManagementView })));
const ProjectManagementViewLazy = lazy(() => import('./ProjectManagementComponents').then(m => ({ default: m.ProjectManagementView })));
const TeacherProjectManagementViewLazy = lazy(() => import('./ProjectManagementComponents').then(m => ({ default: m.TeacherProjectManagementView })));
const ProjectProposalManagementViewLazy = lazy(() => import('./ProjectManagementComponents').then(m => ({ default: m.ProjectProposalManagementView })));
const TranscriptProjectsSection = lazy(() => import('./TranscriptProjectsSection').then(m => ({ default: m.TranscriptProjectsSection })));

// Loading fallback component moved to ./components/ComponentLoader
// Preload components map moved to ./utils/preloadComponents
// Graduation requirement types moved to ./constants/graduation

const App = () => {
  const { currentUser, loadingAuth } = useAuth();
  const [publicTranscriptToken, setPublicTranscriptToken] = useState(null);

  // Check for public transcript URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const transcriptToken = urlParams.get('transcript');
    if (transcriptToken) {
      // Store token in state for public view
      setPublicTranscriptToken(transcriptToken);
    }
  }, []);

  // Profile management state managed by hook
  const {
    userProfile,
    profileForm,
    setProfileForm,
    showProfileEdit,
    setShowProfileEdit,
    handleUpdateProfile
  } = useProfileManagement({ currentUser });

  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [tagFeedbacks, setTagFeedbacks] = useState([]);
  const [tags, setTags] = useState([]);
  const [students, setStudents] = useState([]); // <--- NEW: State to store student list
  const [allUsers, setAllUsers] = useState([]); // All users for name lookup
  const [selectedTag, setSelectedTag] = useState('all');
  const [selectedStudentUid, setSelectedStudentUid] = useState('all'); // <--- NEW: State for selected student filter
  const [teacherExtraFeedback, setTeacherExtraFeedback] = useState({}); // per-post state: { [postId]: { tag: string } }
  // Post creation state managed by hook
  const {
    newPost,
    setNewPost,
    newLink,
    setNewLink,
    newTagJustification,
    setNewTagJustification,
    handleAddLink,
    handleRemoveLink,
    handleAddTagJustification,
    handleRemoveTagJustification
  } = usePostCreation({ currentUser, userProfile });

  const [newComment, setNewComment] = useState({});
  const [newTagFeedback, setNewTagFeedback] = useState({});
  const [newTag, setNewTag] = useState('');
  const [loginForm, setLoginForm] = useState({ email: '', password: '', isSignup: false, isParent: false, linkedStudentUid: '' });
  const [studentView, setStudentView] = useState('resources'); // 'resources' | 'feed' | 'summary' | 'transcript' | 'lor-request' | 'projects' | 'book-logging'
  const [teacherViewMode, setTeacherViewMode] = useState('matrix'); // 'matrix' | 'student-feedback' | 'transcripts' | 'activity-logging' | 'certificates-view' | 'class-management' | 'graduation-requirements' | 'resource-management' | 'lor-management' | 'project-management' | 'book-list-management' | 'book-list-progress' | 'word-of-day-management'
  const [parentView, setParentView] = useState('activity-approval'); // 'activity-approval' | 'transcript' | 'certificate-approval'
  const [sidebarOpen, setSidebarOpen] = useState(false); // Sidebar open/closed state for mobile
  const [headerHeight, setHeaderHeight] = useState(0); // Track header height for button positioning
  
  // UI state managed by hook
  const {
    expandedTags,
    expandedGrades,
    expandedPosts,
    highlightedPostId,
    navigateToPost,
    toggleTagExpansion,
    toggleGradeExpansion,
    togglePostExpansion,
    expandTag,
    collapseAllTags,
    resetUIState
  } = useUIState({ userProfile, posts, selectedStudentUid });
  
  // Post editing state managed by hook
  const {
    editingPostId,
    editPostForm,
    setEditPostForm,
    newTagJustificationEdit,
    setNewTagJustificationEdit,
    newLinkEdit,
    setNewLinkEdit,
    handleEditPost,
    handleAddTagJustificationEdit,
    handleRemoveTagJustificationEdit,
    handleAddLinkEdit,
    handleRemoveLinkEdit,
    handleSaveEditPost,
    handleCancelEditPost
  } = usePostEditing();
  
  // Feedback editing state
  const [editingFeedbackId, setEditingFeedbackId] = useState(null);
  const [editingFeedbackForm, setEditingFeedbackForm] = useState({
    score: '',
    comment: ''
  });
  
  // Transcript settings state
  const [transcriptSettings, setTranscriptSettings] = useState({
    portfolioLink: '',
    aboutMe: '',
    enrollmentStartYear: '',
    enrollmentEndYear: ''
  });
  const [shareableLinks, setShareableLinks] = useState([]);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [selectedStudentForFeedback, setSelectedStudentForFeedback] = useState(null); // Track which student's feedback to show
  // Class management state managed by hook
  const {
    studentClasses,
    classTypes,
    classProposals,
    newClass,
    setNewClass,
    newClassProposal,
    setNewClassProposal,
    editingProposal,
    editProposalForm,
    setEditProposalForm,
    newClassType,
    setNewClassType,
    selectedIcon,
    setSelectedIcon,
    // New workflow states
    selectedStudentId,
    setSelectedStudentId,
    studentAssignmentClasses,
    setStudentAssignmentClasses,
    selectedClassId,
    setSelectedClassId,
    classAssignmentStudents,
    setClassAssignmentStudents,
    // Class editing states
    selectedEditClassName,
    setSelectedEditClassName,
    editClassForm,
    setEditClassForm,
    // Class deletion states
    selectedClassForDeletion,
    setSelectedClassForDeletion,
    // Handlers
    handleAddClassType,
    handleDeleteClassType,
    handleAddFlexibleClass,
    handleAssignClassesToStudent,
    handleAssignStudentsToClass,
    handleUpdateClass,
    handleRemoveClassFromStudent,
    handleDeleteClassEntirely,
    // Class Proposal Handlers
    handleAddClassProposal,
    handleApproveProposal,
    handleRejectProposal,
    handleEditProposal,
    handleSaveProposalEdit,
    handleCancelProposalEdit
  } = useClassManagement({ currentUser, userProfile });
  
  // Graduation requirements management state managed by hook
  const {
    graduationRequirements,
    loading: graduationRequirementsLoading,
    addGraduationRequirement,
    removeGraduationRequirement
  } = useGraduationRequirements({ currentUser });
  
  // Mentor management state managed by hook
  const {
    mentors,
    newMentor,
    setNewMentor
  } = useMentorManagement();

  // Mentor requests state managed by hook
  const {
    mentorRequests
  } = useMentorRequests();
  
  // Letter of Recommendation state managed by hook
  const {
    lorRequests,
    newLorRequest,
    setNewLorRequest,
    handleSubmitLorRequest,
    handleCompleteLorRequest,
    handleDeleteLorRequest
  } = useLorManagement({ currentUser, userProfile });
  
  // Project proposal management state managed by hook
  const {
    projectProposals,
    handleApproveProjectProposal,
    handleRejectProjectProposal,
    handleDeleteProjectProposal
  } = useProjectProposalManagement({ currentUser, userProfile });
  
  // Transcript request management state managed by hook
  const {
    transcriptRequests,
    newTranscriptRequest,
    setNewTranscriptRequest,
    handleSubmitTranscriptRequest,
    handleCompleteTranscriptRequest,
    handleDeleteTranscriptRequest
  } = useTranscriptRequestManagement({ currentUser, userProfile });
  
  // Resource management state managed by hook
  const {
    resources,
    resourceCategories,
    newResource,
    setNewResource,
    editingResourceId,
    editResourceForm,
    setEditResourceForm,
    newCategory,
    setNewCategory,
    editingCategoryId,
    editCategoryName,
    setEditCategoryName,
    handleAddCategory,
    handleEditCategory,
    handleSaveCategory,
    handleCancelEditCategory,
    handleDeleteCategory,
    handleMoveCategoryUp,
    handleMoveCategoryDown,
    handleAddResource,
    handleEditResource,
    handleSaveResource,
    handleCancelEditResource,
    handleDeleteResource,
    handleMoveResourceUp,
    handleMoveResourceDown
  } = useResourceManagement({ currentUser });

  // Word of the Day Management
  const {
    words,
    newWord,
    setNewWord,
    handleAddWord,
    editingWordId,
    editWordForm,
    setEditWordForm,
    handleStartEditWord,
    handleSaveWordEdit,
    handleCancelEditWord,
    handleDeleteWord,
    quotes,
    newQuote,
    setNewQuote,
    handleAddQuote,
    editingQuoteId,
    editQuoteForm,
    setEditQuoteForm,
    handleStartEditQuote,
    handleSaveQuoteEdit,
    handleCancelEditQuote,
    handleDeleteQuote
  } = useWordOfDayManagement({ currentUser });
  
  // Notification system state managed by hook
  const {
    pendingFeedbackNotifications,
    pendingProposalNotifications,
    pendingMentorRequestNotifications,
    pendingLorRequestNotifications,
    pendingTranscriptRequestNotifications,
    pendingProjectProposalNotifications,
    showNotificationDropdown,
    setShowNotificationDropdown
  } = useNotificationSystem({ 
    userProfile, 
    posts, 
    students, 
    tagFeedbacks, 
    classProposals,
    currentUser,
    mentorRequests,
    lorRequests,
    transcriptRequests,
    projectProposals
  });
  // Time logging state managed by hook
  const {
    timeLogs,
    newTimeLog,
    setNewTimeLog,
    expandedTimeLogs,
    handleAddTimeLog,
    handleToggleConsolidatedFeatured,
    toggleTimeLogExpansion,
    handleDeleteConsolidated,
    handleDeleteEntry,
    handleApproveTimeLog,
    editingTimeLogId,
    editTimeLogForm,
    setEditTimeLogForm,
    handleStartEditTimeLog,
    handleSaveTimeLogEdit,
    handleCancelEditTimeLog
  } = useTimeLogging({ currentUser, userProfile }); // New time log entry form
  
  // Book list management state (for teachers)
  const {
    bookLists,
    newBookList,
    setNewBookList,
    handleAddBookList,
    editingBookListId,
    editBookListForm,
    setEditBookListForm,
    handleStartEditBookList,
    handleSaveBookListEdit,
    handleCancelEditBookList,
    handleDeleteBookList,
    newBookEntry,
    setNewBookEntry,
    handleAddBookToList,
    handleRemoveBookFromList,
    handleAddBookToEditList,
    handleRemoveBookFromEditList
  } = useBookListManagement({ currentUser });

  // Book logging state managed by hook
  const {
    bookLogs,
    newBookLog,
    setNewBookLog,
    handleAddBookLog,
    editingBookLogId,
    editBookLogForm,
    setEditBookLogForm,
    handleStartEditBookLog,
    handleSaveBookLogEdit,
    handleCancelEditBookLog,
    handleDeleteBookLog
  } = useBookLogging({ currentUser, userProfile, bookLists });
  
  // Certificate management state managed by hook
  const {
    certificates,
    newCertificate,
    setNewCertificate,
    handleAddCertificate,
    handleToggleCertificateFeatured
  } = useCertificateManagement({ currentUser, userProfile });

  // --- EFFECT 1: Reset app data when user changes ---
  useEffect(() => {
    if (!currentUser) {
      // Reset app data when user logs out
      setPosts([]);
      setComments([]);
      setTagFeedbacks([]);
      setTags([]);
      setStudents([]);
      setAllUsers([]);
      setSelectedStudentUid('all');
      setSelectedTag('all');
      setLoginForm({ email: '', password: '', isSignup: false });
      // Reset UI state (expanded tags, grades, posts)
      resetUIState();
      // Reset view modes to defaults
      setStudentView('resources');
      setTeacherViewMode('matrix');
    }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load transcript settings when user profile changes
  useEffect(() => {
    if (userProfile) {
      setTranscriptSettings({
        portfolioLink: userProfile.portfolioLink || '',
        aboutMe: userProfile.aboutMe || '',
        enrollmentStartYear: userProfile.enrollmentStartYear || '',
        enrollmentEndYear: userProfile.enrollmentEndYear || ''
      });
    }
  }, [userProfile]);

  // Load shareable links for students
  useEffect(() => {
    if (currentUser && userProfile && userProfile.role === 'student') {
      handleGetStudentShareableTranscripts({ studentId: currentUser.uid })
        .then(links => setShareableLinks(links))
        .catch(err => console.error('Error loading shareable links:', err));
    }
  }, [currentUser, userProfile]);

  // Handler for generating new shareable link
  const handleGenerateLink = async () => {
    if (!currentUser) return;
    setGeneratingLink(true);
    try {
      const result = await handleGenerateShareableTranscript({
        studentId: currentUser.uid,
        currentUser
      });
      if (result) {
        // Refresh the list
        const links = await handleGetStudentShareableTranscripts({ studentId: currentUser.uid });
        setShareableLinks(links);
      }
    } catch (error) {
      console.error('Error generating link:', error);
    } finally {
      setGeneratingLink(false);
    }
  };

  // Handler for revoking a shareable link
  const handleRevokeLink = async (token) => {
    if (!currentUser || !window.confirm('Are you sure you want to revoke this shareable link? It will no longer be accessible.')) {
      return;
    }
    try {
      await handleRevokeShareableTranscript({
        token,
        studentId: currentUser.uid,
        currentUser
      });
      // Refresh the list
      const links = await handleGetStudentShareableTranscripts({ studentId: currentUser.uid });
      setShareableLinks(links);
    } catch (error) {
      console.error('Error revoking link:', error);
    }
  };

  // Handler for copying link to clipboard
  const handleCopyLink = (url, linkId) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedLinkId(linkId);
      setTimeout(() => {
        setCopiedLinkId(null);
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy link. Please copy it manually.');
    });
  };

  // Measure header height for button positioning on mobile
  useEffect(() => {
    const updateHeaderHeight = () => {
      const header = document.querySelector('.header');
      if (header) {
        setHeaderHeight(header.offsetHeight);
      }
    };

    // Initial measurement
    updateHeaderHeight();

    // Update on window resize
    window.addEventListener('resize', updateHeaderHeight);
    
    // Also update when sidebar state changes (in case layout shifts)
    const resizeObserver = new ResizeObserver(() => {
      updateHeaderHeight();
    });

    const header = document.querySelector('.header');
    if (header) {
      resizeObserver.observe(header);
    }

    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
      resizeObserver.disconnect();
    };
  }, [userProfile, sidebarOpen]);

  // Preload components based on current view (student)
  useEffect(() => {
    if (!userProfile || userProfile.role !== 'student') return;
    
    // Preload the current view's components
    switch (studentView) {
      case 'feed':
        preloadComponents.studentFeed();
        // Preload likely next view (summary)
        setTimeout(() => preloadComponents.studentSummary(), 1000);
        break;
      case 'summary':
        preloadComponents.studentSummary();
        // Preload likely next views
        setTimeout(() => {
          preloadComponents.studentFeed();
          preloadComponents.studentTranscript();
        }, 1000);
        break;
      case 'transcript':
        preloadComponents.studentTranscript();
        break;
      case 'time-logging':
        preloadComponents.studentTimeLogging();
        break;
      case 'book-logging':
        preloadComponents.studentBookLogging();
        break;
      case 'certificates':
        preloadComponents.studentCertificates();
        break;
      case 'mentors':
        preloadComponents.studentMentors();
        break;
      case 'class-proposals':
        preloadComponents.studentClassProposals();
        break;
      case 'projects':
        preloadComponents.studentProjects();
        break;
      default:
        // No preloading for unknown views
        break;
    }
  }, [studentView, userProfile]);

  // Preload components based on current view (teacher)
  useEffect(() => {
    if (!userProfile || userProfile.role !== 'teacher') return;
    
    // Preload the current view's components
    switch (teacherViewMode) {
      case 'matrix':
        preloadComponents.teacherMatrix();
        // Preload likely next view
        setTimeout(() => preloadComponents.teacherStudentFeedback(), 1000);
        break;
      case 'student-feedback':
        preloadComponents.teacherStudentFeedback();
        // Preload likely next views
        setTimeout(() => {
          preloadComponents.teacherMatrix();
          preloadComponents.teacherTranscripts();
        }, 1000);
        break;
      case 'transcripts':
        preloadComponents.teacherTranscripts();
        break;
      case 'activity-logging':
        preloadComponents.teacherActivityLogging();
        break;
      case 'certificates-view':
        preloadComponents.teacherCertificatesView();
        break;
      case 'class-management':
        preloadComponents.teacherClassManagement();
        break;
      case 'graduation-requirements':
        preloadComponents.teacherGraduationRequirements();
        break;
      case 'mentor-management':
        preloadComponents.teacherMentorManagement();
        break;
      default:
        // No preloading for unknown views
        break;
    }
  }, [teacherViewMode, userProfile]);

  // Firestore subscriptions moved into dedicated hook
  useAppData({
    currentUser,
    loadingAuth,
    setters: { setPosts, setComments, setTagFeedbacks, setTags, setStudents, setAllUsers },
    state: { posts, comments, tagFeedbacks, tags, students, allUsers }
  });

  // Wrapper for handleAddTagFeedback
  const handleAddTagFeedbackWrapper = (postId, tag) => {
    return handleAddTagFeedback({
      postId,
      tag,
      newTagFeedback,
      setNewTagFeedback,
      currentUser,
      userProfile,
      tagFeedbacks,
      posts
    });
  };

  // No new-skill creation required per product requirement


  const handleLogin = () => authFunctions.handleLogin(loginForm, setLoginForm);
  const handlePasswordReset = (email) => authFunctions.handlePasswordReset(email);
  const handleLogout = () => authFunctions.handleLogout();

  // Wrapper for navigateToPost that adds student view switching
  const navigateToPostWrapper = (postId) => {
    // Switch to feed view for students, but keep teacher view mode unchanged
    if (userProfile?.role === 'student') {
      setStudentView('feed');
    }
    // Call the hook's navigateToPost function for the rest
    navigateToPost(postId);
  };

  // Create notification click handler
  const handleNotificationClick = createNotificationClickHandler({
    setShowNotificationDropdown,
    setTeacherViewMode,
    students,
    setSelectedStudentUid,
    setSelectedStudentForFeedback,
    setSelectedTag,
    navigateToPost: navigateToPostWrapper
  });

  // Use utility functions from TimeLoggingComponents
  const consolidateTimeLogs = timeLoggingUtils.consolidateTimeLogs;
  const groupTimeLogsByGrade = timeLoggingUtils.groupTimeLogsByGrade;

  // Helper function to get student display name
  const getStudentDisplayName = (studentId) => {
    const student = students.find(s => s.id === studentId);
    return student?.firstName || student?.name || 'Unknown Student';
  };

  // Wrapper functions for view utilities that need data from state
  const getPostTagFeedbacksWrapper = (postId, tag) => getPostTagFeedbacks(postId, tag, tagFeedbacks);
  const isPostGradedWrapper = (postId) => isPostGraded(postId, tagFeedbacks);
  const getPostCommentsWrapper = (postId) => getPostComments(postId, comments);

  const handleCreatePost = () => postCreationFunctions.handleCreatePost(newPost, currentUser, userProfile, setNewPost);

  // Wrapper for handleAddComment
  const handleAddCommentWrapper = (postId) => {
    return handleAddComment({
      postId,
      newComment,
      setNewComment,
      currentUser,
      userProfile
    });
  };

  // Feedback editing handlers
  const handleEditFeedback = (feedback) => {
    setEditingFeedbackId(feedback.id);
    setEditingFeedbackForm({
      score: feedback.score || '',
      comment: feedback.comment || ''
    });
  };

  const handleSaveFeedback = async (feedbackId) => {
    const score = Number(editingFeedbackForm.score || 0);
    const comment = editingFeedbackForm.comment.trim();
    
    if (!comment && !(score >= 1 && score <= 5)) {
      alert('Please provide a score (1-5) or a comment.');
      return;
    }
    
    await handleUpdateTagFeedback({
      feedbackId,
      comment,
      score
    });
    
    setEditingFeedbackId(null);
    setEditingFeedbackForm({ score: '', comment: '' });
  };

  const handleCancelEditFeedback = () => {
    setEditingFeedbackId(null);
    setEditingFeedbackForm({ score: '', comment: '' });
  };

  const handleDeleteFeedbackWrapper = async (feedbackId) => {
    await handleDeleteTagFeedback({ feedbackId });
  };

  // Wrapper for handleAddTag
  const handleAddTagWrapper = async () => {
    return handleAddTag({ newTag, tags, setNewTag });
  };

  // Wrapper for handleDeleteTag
  const handleDeleteTagWrapper = async (tagToDelete) => {
    return handleDeleteTag({ tagToDelete, tags, currentUser });
  };

  const handleToggleFeatured = (postId, currentFeatured) => {
    return postActionHandlers.handleToggleFeatured(postId, currentFeatured, posts, currentUser, setPosts);
  };

  // Post action handlers - now using wrappers for the handlers from PostComponents
  const handleToggleHideFromTranscript = (postId, currentHidden) => {
    return postActionHandlers.handleToggleHideFromTranscript(postId, currentHidden, setPosts);
  };

  const handleDeletePost = (postId) => {
    return postActionHandlers.handleDeletePost(postId, setPosts);
  };

  // Wrapper for handleSaveEditPost to pass setPosts
  const handleSaveEditPostWrapper = () => {
    return handleSaveEditPost(setPosts);
  };


  // Wrapper for handleUpdateTranscriptSettings
  const handleUpdateTranscriptSettingsWrapper = async () => {
    return handleUpdateTranscriptSettings({ transcriptSettings, currentUser });
  };

  const handleDownloadTranscriptPDF = async () => {
    if (!selectedStudentUid || selectedStudentUid === 'all') {
      alert('Please select a specific student to download their transcript.');
      return;
    }

    const student = students.find(s => s.id === selectedStudentUid);
    if (!student) {
      alert('Student not found.');
      return;
    }

    try {
      // Preload PDF components if not already loaded
      preloadComponents.pdf();
      
      // Wait for both modules to load
      const [TranscriptPDFModule, pdfRendererModule] = await Promise.all([
        import('./TranscriptPDF'),
        import('@react-pdf/renderer')
      ]);
      
      const TranscriptPDFComponent = TranscriptPDFModule.default;
      const { pdf } = pdfRendererModule;
      
      // Get student-specific data
      const filteredStudentPosts = posts.filter(post => post.userId === selectedStudentUid);
      const filteredStudentTimeLogs = timeLogs.filter(log => log.userId === selectedStudentUid);
      const filteredStudentCertificates = certificates.filter(cert => cert.userId === selectedStudentUid);
      
      // Calculate skill averages for the student
      const studentTagFeedbacks = tagFeedbacks.filter(tf => 
        filteredStudentPosts.some(post => post.id === tf.postId)
      );
      
      const skillAverages = {};
      studentTagFeedbacks.forEach(feedback => {
        if (!skillAverages[feedback.tag]) {
          skillAverages[feedback.tag] = { total: 0, count: 0 };
        }
        skillAverages[feedback.tag].total += feedback.score;
        skillAverages[feedback.tag].count += 1;
      });
      
      // Convert to average format
      Object.keys(skillAverages).forEach(skill => {
        skillAverages[skill] = {
          average: skillAverages[skill].total / skillAverages[skill].count,
          count: skillAverages[skill].count
        };
      });

      // Generate PDF
      const doc = <TranscriptPDFComponent 
        studentName={student.name}
        studentFirstName={student.firstName}
        studentLastName={student.lastName}
        studentId={selectedStudentUid}
        studentClasses={studentClasses}
        posts={filteredStudentPosts}
        timeLogs={filteredStudentTimeLogs}
        certificates={filteredStudentCertificates}
        skillAverages={skillAverages}
        tagFeedbacks={tagFeedbacks}
        tags={tags}
        classTypes={classTypes}
        portfolioLink={student.portfolioLink}
        aboutMe={student.aboutMe}
        enrollmentStartYear={student.enrollmentStartYear}
        enrollmentEndYear={student.enrollmentEndYear}
        graduationRequirements={graduationRequirements}
        requirementTypes={GRADUATION_REQUIREMENT_TYPES}
      />;

      const asPdf = pdf(doc);
      const blob = await asPdf.toBlob();
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(student.firstName || student.name || 'Student').replace(/\s+/g, '_')}_Transcript.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // --- Render logic: Determines what JSX to return based on state ---

  // 0. If there's a public transcript token, show public view (no auth required)
  if (publicTranscriptToken) {
    return <PublicTranscriptView token={publicTranscriptToken} />;
  }

  // 1. Show global loading screen while Firebase Auth initializes
  if (loadingAuth) {
    return <AuthLoading message="Loading application data..." />;
  }

  // 2. If auth is loaded but no user is logged in, show the login UI
  if (!currentUser) {
    return <LoginForm loginForm={loginForm} setLoginForm={setLoginForm} handleLogin={handleLogin} handlePasswordReset={handlePasswordReset} />;
  }

  // 3. If a user is logged in, but their profile data hasn't loaded yet
  if (!userProfile) {
    return <AuthLoading message="Loading user profile..." />;
  }

  // Email verification gating removed per product decision

  // 4. If everything is loaded, render the main application
  const currentUserRole = userProfile.role;

  try {
    return (
    <div className="app-container">
      <UserInfoHeader
        userProfile={userProfile}
        currentUser={currentUser}
        currentUserRole={currentUserRole}
        pendingFeedbackNotifications={pendingFeedbackNotifications}
        pendingProposalNotifications={pendingProposalNotifications}
        pendingMentorRequestNotifications={pendingMentorRequestNotifications}
        pendingLorRequestNotifications={pendingLorRequestNotifications}
        pendingTranscriptRequestNotifications={pendingTranscriptRequestNotifications}
        pendingProjectProposalNotifications={pendingProjectProposalNotifications}
        showNotificationDropdown={showNotificationDropdown}
        setShowNotificationDropdown={setShowNotificationDropdown}
        handleNotificationClick={handleNotificationClick}
        setShowProfileEdit={setShowProfileEdit}
        handleLogout={handleLogout}
      />

      {/* Notification Dropdown - Outside Header */}
      {userProfile?.role === 'teacher' && showNotificationDropdown && (
        <NotificationDropdown 
          pendingFeedbackNotifications={pendingFeedbackNotifications}
          pendingProposalNotifications={pendingProposalNotifications}
          pendingMentorRequestNotifications={pendingMentorRequestNotifications}
          pendingLorRequestNotifications={pendingLorRequestNotifications}
          pendingTranscriptRequestNotifications={pendingTranscriptRequestNotifications}
          pendingProjectProposalNotifications={pendingProjectProposalNotifications}
          handleNotificationClick={handleNotificationClick}
        />
      )}

      <ProfileEditModal
        showProfileEdit={showProfileEdit}
        setShowProfileEdit={setShowProfileEdit}
        profileForm={profileForm}
        setProfileForm={setProfileForm}
        handleUpdateProfile={handleUpdateProfile}
        userRole={currentUserRole}
        currentUserUid={currentUser?.uid}
      />

      {/* Sidebar Toggle Button - Mobile Only */}
      <button 
        className={`sidebar-toggle ${sidebarOpen ? 'sidebar-toggle-open' : ''}`}
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Toggle sidebar"
        style={headerHeight > 0 ? { '--header-height': `${headerHeight}px` } : {}}
      >
        <i className={`fa-solid ${sidebarOpen ? 'fa-times' : 'fa-bars'}`}></i>
      </button>

      {/* Sidebar Overlay - Mobile Only */}
      {sidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="main-content">
        <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
          {currentUserRole === 'parent' && (
            <div className="add-tag-section">
              <h3>Parent</h3>
              <button
                className={parentView === 'activity-approval' ? 'tag-filter active' : 'tag-filter'}
                onClick={() => setParentView('activity-approval')}
              >
                Activity Approval
              </button>
              <button
                className={parentView === 'certificate-approval' ? 'tag-filter active' : 'tag-filter'}
                onClick={() => setParentView('certificate-approval')}
              >
                Certificate Approval
              </button>
              <button
                className={parentView === 'transcript' ? 'tag-filter active' : 'tag-filter'}
                onClick={() => setParentView('transcript')}
              >
                Unofficial Transcript
              </button>

            </div>
          )}
          {currentUserRole === 'student' && (
            <div className="add-tag-section">
              <button
                className={studentView === 'resources' ? 'tag-filter active' : 'tag-filter'}
                onClick={() => setStudentView('resources')}
              >
                Resources
              </button>
              <h3>Progress Tracking</h3>

              <button
                className={studentView === 'projects' ? 'tag-filter active' : 'tag-filter'}
                onClick={() => setStudentView('projects')}
              >
                Project Management
              </button>
              <button
                className={studentView === 'feed' ? 'tag-filter active' : 'tag-filter'}
                onClick={() => setStudentView('feed')}
              >
                Reflections
              </button>
              <button
                className={studentView === 'summary' ? 'tag-filter active' : 'tag-filter'}
                onClick={() => setStudentView('summary')}
              >
                Competency Progress
              </button>
              <h3>Requests and Help</h3>
              <button
                className={studentView === 'mentors' ? 'tag-filter active' : 'tag-filter'}
                onClick={() => setStudentView('mentors')}
              >
                Mentors
              </button>
              <button
                className={studentView === 'requests' ? 'tag-filter active' : 'tag-filter'}
                onClick={() => setStudentView('requests')}
              >
                Requests
              </button>
              <h3>Logging</h3>
              <button
                className={studentView === 'certificates' ? 'tag-filter active' : 'tag-filter'}
                onClick={() => setStudentView('certificates')}
              >
                Certificates & Awards
              </button>
              <button
                className={studentView === 'time-logging' ? 'tag-filter active' : 'tag-filter'}
                onClick={() => setStudentView('time-logging')}
              >
                Activity Log
              </button>
              <button
                className={studentView === 'book-logging' ? 'tag-filter active' : 'tag-filter'}
                onClick={() => setStudentView('book-logging')}
              >
                Book Log
              </button>
              <button
                className={studentView === 'transcript' ? 'tag-filter active' : 'tag-filter'}
                onClick={() => setStudentView('transcript')}
              >
                Transcript
              </button>


            </div>
          )}

          

          {currentUserRole === 'teacher' && (
            <div className="add-tag-section">
              <h3>All Students</h3>
              <button
                className={teacherViewMode === 'matrix' ? 'tag-filter active teacher-view-btn' : 'tag-filter teacher-view-btn'}
                onClick={() => { 
                  setTeacherViewMode('matrix');
                  setSelectedStudentUid('all');
                  setSelectedTag('all');
                  collapseAllTags();
                }}
              >
                Competency Matrix
              </button>

              <h3>Student Progress</h3>
              <button
                className={teacherViewMode === 'student-feedback' ? 'tag-filter active teacher-view-btn' : 'tag-filter teacher-view-btn'}
                onClick={() => { 
                  setTeacherViewMode('student-feedback');
                  if (students && students.length > 0) {
                    setSelectedStudentUid(students[0].id);
                    setSelectedStudentForFeedback(students[0]);
                  } else {
                    setSelectedStudentUid('all');
                  }
                  setSelectedTag('all');
                }}
              >
                Student Progress
              </button>
              <button
                className={teacherViewMode === 'project-management' ? 'tag-filter active teacher-view-btn' : 'tag-filter teacher-view-btn'}
                onClick={() => { 
                  setTeacherViewMode('project-management');
                  setSelectedStudentUid('all');
                  setSelectedTag('all');
                }}
              >
                Project Management
              </button>
              <button
                className={teacherViewMode === 'activity-logging' ? 'tag-filter active teacher-view-btn' : 'tag-filter teacher-view-btn'}
                onClick={() => { 
                  setTeacherViewMode('activity-logging');
                  if (students && students.length > 0) {
                    setSelectedStudentUid(students[0].id);
                  } else {
                    setSelectedStudentUid('all');
                  }
                  setSelectedTag('all');
                }}
              >
                Activity Logging
              </button>
              <button
                className={teacherViewMode === 'book-list-progress' ? 'tag-filter active teacher-view-btn' : 'tag-filter teacher-view-btn'}
                onClick={() => { 
                  setTeacherViewMode('book-list-progress');
                  setSelectedStudentUid('all');
                  setSelectedTag('all');
                }}
              >
                Book List Progress
              </button>
              <button
                className={teacherViewMode === 'certificates-view' ? 'tag-filter active teacher-view-btn' : 'tag-filter teacher-view-btn'}
                onClick={() => { 
                  setTeacherViewMode('certificates-view');
                  if (students && students.length > 0) {
                    setSelectedStudentUid(students[0].id);
                  } else {
                    setSelectedStudentUid('all');
                  }
                  setSelectedTag('all');
                }}
              >
                Certificates & Awards
              </button>
              <button
                className={teacherViewMode === 'transcripts' ? 'tag-filter active teacher-view-btn' : 'tag-filter teacher-view-btn'}
                onClick={() => { 
                  setTeacherViewMode('transcripts');
                  if (students && students.length > 0) {
                    setSelectedStudentUid(students[0].id);
                  } else {
                    setSelectedStudentUid('all');
                  }
                  setSelectedTag('all');
                }}
              >
                Transcripts
              </button>
              <button
                className={teacherViewMode === 'graduation-requirements' ? 'tag-filter active teacher-view-btn' : 'tag-filter teacher-view-btn'}
                onClick={() => { 
                  setTeacherViewMode('graduation-requirements');
                  setSelectedStudentUid('all');
                  setSelectedTag('all');
                }}
              >
                Graduation Requirements
              </button>

              
            </div>
          )}

          {currentUserRole === 'teacher' && (teacherViewMode === 'student-feedback' || teacherViewMode === 'transcripts' || teacherViewMode === 'activity-logging' || teacherViewMode === 'certificates-view') && (
            <div className="add-tag-section">
              <h3>Filter by Student</h3>
              {(students || []).map(student => (
                <button
                  key={student.id}
                  className={selectedStudentUid === student.id ? 'tag-filter active' : 'tag-filter'}
                  onClick={() => { 
                    setSelectedStudentUid(student.id); 
                    setSelectedTag('all'); 
                    if (teacherViewMode === 'student-feedback') {
                    setSelectedStudentForFeedback(student);
                    }
                  }}
                >
                  {student.firstName || student.name || student.id}
                </button>
              ))}
            </div>
          )}

          {currentUserRole === 'teacher' && (
            <div className="add-tag-section">
              <h3>Management</h3>
              <button
                className={teacherViewMode === 'class-management' ? 'tag-filter active teacher-view-btn' : 'tag-filter teacher-view-btn'}
                onClick={() => { 
                  setTeacherViewMode('class-management');
                  setSelectedStudentUid('all');
                  setSelectedTag('all');
                }}
              >
                Class Management
              </button>
              <button
                className={teacherViewMode === 'mentor-management' ? 'tag-filter active teacher-view-btn' : 'tag-filter teacher-view-btn'}
                onClick={() => { 
                  setTeacherViewMode('mentor-management');
                  setSelectedStudentUid('all');
                  setSelectedTag('all');
                }}
              >
                Mentor Management
              </button>
              <button
                className={teacherViewMode === 'resource-management' ? 'tag-filter active teacher-view-btn' : 'tag-filter teacher-view-btn'}
                onClick={() => { 
                  setTeacherViewMode('resource-management');
                  setSelectedStudentUid('all');
                  setSelectedTag('all');
                }}
              >
                Resource Management
              </button>
              <button
                className={teacherViewMode === 'word-of-day-management' ? 'tag-filter active teacher-view-btn' : 'tag-filter teacher-view-btn'}
                onClick={() => { 
                  setTeacherViewMode('word-of-day-management');
                  setSelectedStudentUid('all');
                  setSelectedTag('all');
                }}
              >
                Word of the Day
              </button>
              <button
                className={teacherViewMode === 'book-list-management' ? 'tag-filter active teacher-view-btn' : 'tag-filter teacher-view-btn'}
                onClick={() => { 
                  setTeacherViewMode('book-list-management');
                  setSelectedStudentUid('all');
                  setSelectedTag('all');
                }}
              >
                Book List Management
              </button>
              <button
                className={teacherViewMode === 'lor-management' ? 'tag-filter active teacher-view-btn' : 'tag-filter teacher-view-btn'}
                onClick={() => { 
                  setTeacherViewMode('lor-management');
                  setSelectedStudentUid('all');
                  setSelectedTag('all');
                }}
              >
                Document Requests
              </button>
            </div>
          )}
          {currentUserRole === 'teacher' && teacherViewMode === 'class-management' && (
            <div className="add-tag-section">
              <h3>Manage Competencies</h3>
              <input
                type="text"
                placeholder="New Competency"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddTagWrapper()}
              />
              <button onClick={handleAddTagWrapper}>Add Competency</button>
              <div className="tag-list">
                {tags.map((tag) => (
                  <div key={tag} className="tag-item">
                    <span className="tag-name">{tag}</span>
                    <button
                      onClick={() => handleDeleteTagWrapper(tag)}
                      className="tag-delete-btn"
                      title="Delete competency"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {currentUserRole === 'teacher' && teacherViewMode === 'class-management' && (
            <Suspense fallback={<div className="sidebar-loader">Loading...</div>}>
              <ClassTypeManagement
                classTypes={classTypes}
                newClassType={newClassType}
                setNewClassType={setNewClassType}
                selectedIcon={selectedIcon}
                setSelectedIcon={setSelectedIcon}
                handleAddClassType={handleAddClassType}
                handleDeleteClassType={handleDeleteClassType}
              />
            </Suspense>
          )}
        </aside>

        <main className="feed">
          {currentUserRole === 'teacher' && teacherViewMode === 'matrix' && (
            <Suspense fallback={<ComponentLoader />}>
              <StudentScoreMatrix 
                students={students}
                tags={tags}
                tagFeedbacks={tagFeedbacks}
                onCellClick={(studentId, tag) => {
                  // Switch to student feedback view
                  setTeacherViewMode('student-feedback');
                  
                  // Find and select the student
                  const student = students.find(s => s.id === studentId);
                  if (student) {
                    setSelectedStudentUid(studentId);
                    setSelectedStudentForFeedback(student);
                  }
                  
                  // Set the tag filter to 'all' to show skill summary instead of posts
                  setSelectedTag('all');
                  
                  // Expand the clicked skill to show feedback in skill summary
                  expandTag(tag);
                  
                  // Scroll to the specific skill section after a brief delay to allow rendering
                  setTimeout(() => {
                    const skillElement = document.querySelector(`[data-skill-tag="${tag}"]`);
                    if (skillElement) {
                      skillElement.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'start',
                        inline: 'nearest'
                      });
                    }
                  }, 100);
                }}
              />
            </Suspense>
          )}

          {currentUserRole === 'student' && studentView === 'summary' && (
            <div className="post post-margin-bottom">
              <h2>Competencies Progress</h2>
              
              <CompetencySummary
                studentId={currentUser.uid}
                tagFeedbacks={tagFeedbacks}
                posts={posts}
                expandedTags={expandedTags}
                toggleTagExpansion={toggleTagExpansion}
                navigateToPost={navigateToPostWrapper}
                noFeedbackMessage="No feedback yet."
              />
              
              {/* Competencies Summary */}
              <SkillAveragesGrid
                studentId={currentUser.uid}
                tags={tags}
                tagFeedbacks={tagFeedbacks}
              />
            </div>
          )}

          {/* Student Resources Landing Page */}
          {currentUserRole === 'student' && studentView === 'resources' && (
            <Suspense fallback={<ComponentLoader />}>
              <ResourcesLandingPage
                resources={resources}
                resourceCategories={resourceCategories}
                words={words}
                quotes={quotes}
              />
            </Suspense>
          )}

          {currentUserRole === 'student' && studentView === 'transcript' && (
            <div className="post post-margin-bottom">
              <h2>Unofficial Transcript</h2>
              <p className="transcript-description">
                Overview of all your posts, links, and skill scores
              </p>
              
              <Suspense fallback={<ComponentLoader />}>
                <TranscriptProjectsSection
                  studentId={currentUser.uid}
                  posts={posts}
                  tagFeedbacks={tagFeedbacks}
                  tags={tags}
                  studentClasses={studentClasses}
                  classTypes={classTypes}
                  certificates={certificates}
                  timeLogs={timeLogs}
                  currentUser={currentUser}
                  consolidateTimeLogs={consolidateTimeLogs}
                  groupTimeLogsByGrade={groupTimeLogsByGrade}
                />
              </Suspense>
            </div>
          )}

          {currentUserRole === 'student' && studentView === 'feed' && (
            <Suspense fallback={<ComponentLoader />}>
              <PostCreationForm
                newPost={newPost}
                setNewPost={setNewPost}
                newLink={newLink}
                setNewLink={setNewLink}
                newTagJustification={newTagJustification}
                setNewTagJustification={setNewTagJustification}
                tags={tags}
                studentClasses={studentClasses}
                classTypes={classTypes}
                currentUser={currentUser}
                handleCreatePost={handleCreatePost}
                handleAddTagJustification={handleAddTagJustification}
                handleRemoveTagJustification={handleRemoveTagJustification}
                handleAddLink={handleAddLink}
                handleRemoveLink={handleRemoveLink}
              />
            </Suspense>
          )}

          {/* Transcript Settings Form */}
          {currentUserRole === 'student' && studentView === 'transcript' && (
            <div className="create-post">
              <h2>Transcript Settings</h2>
              <div className="form-group">
                <label>Portfolio Link:</label>
                <input
                  type="text"
                  placeholder="Portfolio Link (e.g., https://your-portfolio-website.com)"
                  value={transcriptSettings.portfolioLink}
                  onChange={(e) => setTranscriptSettings({ ...transcriptSettings, portfolioLink: e.target.value })}
                  className="post-title-input"
                />
              </div>
              <div className="form-group">
                <label>About Me:</label>
                <textarea
                  placeholder="About Me - Describe yourself in 100 words or less. This will appear on your transcript."
                  value={transcriptSettings.aboutMe}
                  onChange={(e) => setTranscriptSettings({ ...transcriptSettings, aboutMe: e.target.value })}
                  rows={4}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Enrollment Start Year:</label>
                  <input
                    type="number"
                    placeholder="e.g., 2026"
                    value={transcriptSettings.enrollmentStartYear}
                    onChange={(e) => setTranscriptSettings({ ...transcriptSettings, enrollmentStartYear: e.target.value })}
                    className="post-title-input"
                    min="2026"
                    max="3000"
                  />
                </div>
                <div className="form-group">
                  <label>Enrollment End Year:</label>
                  <input
                    type="number"
                    placeholder="e.g., 2030"
                    value={transcriptSettings.enrollmentEndYear}
                    onChange={(e) => setTranscriptSettings({ ...transcriptSettings, enrollmentEndYear: e.target.value })}
                    className="post-title-input"
                    min="2027"
                    max="3000"
                  />
                </div>
              </div>
              <button 
                className="log-activity-button" 
                onClick={handleUpdateTranscriptSettingsWrapper}
              >
                Update Transcript Settings
              </button>
            </div>
          )}

          {/* Shareable Transcript Links */}
          {currentUserRole === 'student' && studentView === 'transcript' && (
            <div className="create-post">
              <h2>Shareable Transcript Links</h2>
              <p className="transcript-description">
                Generate a shareable link to your unofficial transcript that can be accessed by anyone without logging in.
              </p>
              <p>This transcript will only show featured projects and activities.</p>
              <br />
              <div className="form-group">
                <button 
                  className="log-activity-button" 
                  onClick={handleGenerateLink}
                  disabled={generatingLink}
                >
                  {generatingLink ? 'Generating...' : 'Generate New Shareable Link'}
                </button>
              </div>

              {shareableLinks.length > 0 && (
                <div className="shareable-links-container">
                  <h3>Active Shareable Links</h3>
                  {shareableLinks.map((link) => (
                    <div key={link.id} className="shareable-link-item">
                      <div className="shareable-link-info">
                        <div className="shareable-link-url">
                          <input
                            type="text"
                            readOnly
                            value={link.publicUrl}
                            className="shareable-link-input"
                            onClick={(e) => e.target.select()}
                          />
                        </div>
                        <div className="shareable-link-meta">
                          <span>Created: {new Date(link.createdAt).toLocaleDateString()}</span>
                          {link.accessCount > 0 && (
                            <span> • Accessed {link.accessCount} time{link.accessCount !== 1 ? 's' : ''}</span>
                          )}
                          {link.lastAccessedAt && (
                            <span> • Last accessed: {new Date(link.lastAccessedAt).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                      <div className="shareable-link-actions">
                        <button
                          className="proposal-edit-btn"
                          onClick={() => handleCopyLink(link.publicUrl, link.id)}
                          title="Copy link to clipboard"
                          style={{ minWidth: '100px' }}
                        >
                          <i className={`fa-solid ${copiedLinkId === link.id ? 'fa-check' : 'fa-copy'}`}></i> {copiedLinkId === link.id ? 'Copied' : 'Copy'}
                        </button>
                        <button
                          className="proposal-reject-btn"
                          onClick={() => handleRevokeLink(link.token)}
                          title="Revoke this link"
                        >
                          <i className="fa-solid fa-ban"></i> Revoke
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {shareableLinks.length === 0 && (
                <p className="no-posts no-posts-padding">
                  No shareable links yet. Click "Generate New Shareable Link" to create one.
                </p>
              )}
            </div>
          )}

          {currentUserRole === 'student' && studentView === 'time-logging' && (
            <Suspense fallback={<ComponentLoader />}>
              <TimeLogCreationForm
                newTimeLog={newTimeLog}
                setNewTimeLog={setNewTimeLog}
                handleAddTimeLog={handleAddTimeLog}
              />
            </Suspense>
          )}

          {currentUserRole === 'student' && studentView === 'book-logging' && (
            <>
              <Suspense fallback={<ComponentLoader />}>
                <BookLogCreationForm
                  newBookLog={newBookLog}
                  setNewBookLog={setNewBookLog}
                  handleAddBookLog={handleAddBookLog}
                  bookLists={bookLists}
                />
              </Suspense>
            </>
          )}

          {currentUserRole === 'student' && studentView === 'certificates' && (
            <Suspense fallback={<ComponentLoader />}>
              <CertificateCreationForm
                newCertificate={newCertificate}
                setNewCertificate={setNewCertificate}
                handleAddCertificate={handleAddCertificate}
              />
            </Suspense>
          )}

          {/* Student Requests Page - Class Proposals, Letter of Recommendation Requests, and Transcript Requests */}
          {currentUserRole === 'student' && studentView === 'requests' && (
            <>
              <Suspense fallback={<ComponentLoader />}>
                <ClassProposalForm
                  newClassProposal={newClassProposal}
                  setNewClassProposal={setNewClassProposal}
                  classTypes={classTypes}
                  currentUser={currentUser}
                  userProfile={userProfile}
                  handleAddClassProposal={handleAddClassProposal}
                />
              </Suspense>
              
              <Suspense fallback={<ComponentLoader />}>
                <LorRequestForm
                  newLorRequest={newLorRequest}
                  setNewLorRequest={setNewLorRequest}
                  handleSubmitLorRequest={handleSubmitLorRequest}
                />
              </Suspense>
              
              <Suspense fallback={<ComponentLoader />}>
                <LorRequestsDisplay
                  lorRequests={lorRequests}
                  currentUser={currentUser}
                />
              </Suspense>
              
              <Suspense fallback={<ComponentLoader />}>
                <TranscriptRequestForm
                  newTranscriptRequest={newTranscriptRequest}
                  setNewTranscriptRequest={setNewTranscriptRequest}
                  handleSubmitTranscriptRequest={handleSubmitTranscriptRequest}
                />
              </Suspense>
              
              <Suspense fallback={<ComponentLoader />}>
                <TranscriptRequestsDisplay
                  transcriptRequests={transcriptRequests}
                  currentUser={currentUser}
                />
              </Suspense>
            </>
          )}

          {/* Student Project Management */}
          {currentUserRole === 'student' && studentView === 'projects' && (
            <Suspense fallback={<ComponentLoader />}>
              <ProjectManagementViewLazy currentUser={currentUser} userProfile={userProfile} />
            </Suspense>
          )}

          {currentUserRole === 'student' && studentView === 'time-logging' && (
            <Suspense fallback={<ComponentLoader />}>
              <TimeLogDisplay
                timeLogs={timeLogs}
                currentUser={currentUser}
                expandedTimeLogs={expandedTimeLogs}
                toggleTimeLogExpansion={toggleTimeLogExpansion}
                handleToggleConsolidatedFeatured={handleToggleConsolidatedFeatured}
                consolidateTimeLogs={consolidateTimeLogs}
                groupTimeLogsByGrade={groupTimeLogsByGrade}
                editingTimeLogId={editingTimeLogId}
                editTimeLogForm={editTimeLogForm}
                setEditTimeLogForm={setEditTimeLogForm}
                handleStartEditTimeLog={handleStartEditTimeLog}
                handleSaveTimeLogEdit={handleSaveTimeLogEdit}
                handleCancelEditTimeLog={handleCancelEditTimeLog}
                handleDeleteConsolidated={handleDeleteConsolidated}
                handleDeleteEntry={handleDeleteEntry}
              />
            </Suspense>
          )}

          {currentUserRole === 'student' && studentView === 'book-logging' && (
            <>
              <Suspense fallback={<ComponentLoader />}>
                <BookLogDisplay
                  bookLogs={bookLogs}
                  currentUser={currentUser}
                  editingBookLogId={editingBookLogId}
                  editBookLogForm={editBookLogForm}
                  setEditBookLogForm={setEditBookLogForm}
                  handleStartEditBookLog={handleStartEditBookLog}
                  handleSaveBookLogEdit={handleSaveBookLogEdit}
                  handleCancelEditBookLog={handleCancelEditBookLog}
                  handleDeleteBookLog={handleDeleteBookLog}
                  bookLists={bookLists}
                />
              </Suspense>
              <Suspense fallback={<ComponentLoader />}>
                <BookListProgressViewLazy
                  bookLists={bookLists}
                  bookLogs={bookLogs}
                  currentUser={currentUser}
                />
              </Suspense>
            </>
          )}

          {currentUserRole === 'student' && studentView === 'certificates' && (
            <Suspense fallback={<ComponentLoader />}>
              <CertificateDisplay
                certificates={certificates}
                currentUser={currentUser}
                handleToggleCertificateFeatured={handleToggleCertificateFeatured}
              />
            </Suspense>
          )}

          {/* Parent: Read-only transcript for linked student */}
          {currentUserRole === 'parent' && parentView === 'activity-approval' && (
            <div className="post post-margin-bottom">
              <h2>Activity Approval</h2>
              {!userProfile.linkedStudentUid ? (
                <p className="no-posts">No student linked to this account yet. Please log out and sign up again as a parent with your student's ID.</p>
              ) : (
                (() => {
                  const pendingChildLogs = (timeLogs || []).filter(t => t.userId === userProfile.linkedStudentUid && t.status !== 'approved');
                  if (pendingChildLogs.length === 0) {
                    return <p className="no-posts no-posts-padding">No activities awaiting approval.</p>;
                  }
                  return (
                    <div className="create-post approval-block">
                      <h3>Pending Activity Approvals</h3>
                      <p className="section-description">Review and edit your student's activity details before approving.</p>
                      <div className="proposal-list">
                        {pendingChildLogs.map(entry => {
                          const isEditing = editingTimeLogId === entry.id;
                          return (
                            <div key={entry.id} className="proposal-item">
                              <div className="proposal-header">
                                <div className="proposal-title">
                                  {isEditing ? (
                                    <input
                                      type="text"
                                      value={editTimeLogForm.activity}
                                      onChange={(e) => setEditTimeLogForm({ ...editTimeLogForm, activity: e.target.value })}
                                      className="proposal-edit-input"
                                    />
                                  ) : (
                                    entry.activity
                                  )}
                                </div>
                                <div className="proposal-meta">
                                  <strong>{entry.hours} hrs</strong> · {new Date(entry.startDate).toLocaleDateString()} - {new Date(entry.endDate).toLocaleDateString()}
                                </div>
                              </div>
                              <div className="proposal-description">
                                {isEditing ? (
                                  <>
                                    <div className="form-row">
                                      <div className="form-group half-width">
                                        <label>Domain</label>
                                        <select
                                          value={editTimeLogForm.domain}
                                          onChange={(e) => setEditTimeLogForm({ ...editTimeLogForm, domain: e.target.value })}
                                          className="project-type-dropdown"
                                        >
                                          <option value="">Select Domain</option>
                                          <option value="physical-activity">Physical Activity</option>
                                          <option value="internship">Internship/Work Experience</option>
                                          <option value="volunteer">Volunteer Work</option>
                                          <option value="clubs-&-classes">Clubs and Classes</option>
                                          <option value="in-school-leadership-position">In School Leadership Position</option>
                                          <option value="other">Other</option>
                                        </select>
                                      </div>
                                      <div className="form-group half-width">
                                        <label>Hours</label>
                                        <input type="number" step="0.5" min="0" value={editTimeLogForm.hours} onChange={(e) => setEditTimeLogForm({ ...editTimeLogForm, hours: e.target.value })} className="post-title-input" />
                                      </div>
                                    </div>
                                    <div className="form-row">
                                      <div className="form-group half-width">
                                        <label>Start Date</label>
                                        <input type="date" value={editTimeLogForm.startDate} onChange={(e) => setEditTimeLogForm({ ...editTimeLogForm, startDate: e.target.value })} className="post-title-input" />
                                      </div>
                                      <div className="form-group half-width">
                                        <label>End Date</label>
                                        <input type="date" value={editTimeLogForm.endDate} onChange={(e) => setEditTimeLogForm({ ...editTimeLogForm, endDate: e.target.value })} className="post-title-input" />
                                      </div>
                                    </div>
                                    <textarea value={editTimeLogForm.description} onChange={(e) => setEditTimeLogForm({ ...editTimeLogForm, description: e.target.value })} className="proposal-edit-textarea" rows="3" placeholder="Additional details..." />
                                  </>
                                ) : (
                                  entry.description || <em>No description provided</em>
                                )}
                              </div>
                              <div className="proposal-footer">
                                <div className="proposal-student">Pending · {entry.domain.replace(/-/g, ' ').replace(/&/g, '&').replace(/\b\w/g, l => l.toUpperCase())}</div>
                                <div className="proposal-actions">
                                  {isEditing ? (
                                    <>
                                      <button className="proposal-save-btn" onClick={handleSaveTimeLogEdit}>Save</button>
                                      <button className="proposal-cancel-btn" onClick={handleCancelEditTimeLog}>Cancel</button>
                                    </>
                                  ) : (
                                    <>
                                      <button className="proposal-edit-btn" onClick={() => handleStartEditTimeLog(entry)}>Edit</button>
                                      <button className="proposal-approve-btn" onClick={() => handleApproveTimeLog(entry.id)}>Approve</button>
                                      <button className="proposal-reject-btn" onClick={() => handleDeleteEntry(entry.id)}>Reject</button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()
              )}
            </div>
          )}

          {currentUserRole === 'parent' && parentView === 'transcript' && (
            <div className="post post-margin-bottom">
              <h2>My Student's Unofficial Transcript</h2>
              {!userProfile.linkedStudentUid ? (
                <p className="no-posts">No student linked to this account yet. Please log out and sign up again as a parent with your student's ID.</p>
              ) : (
                <Suspense fallback={<ComponentLoader />}>
                  <TranscriptProjectsSection
                    studentId={userProfile.linkedStudentUid}
                    posts={posts}
                    tagFeedbacks={tagFeedbacks}
                    tags={tags}
                    studentClasses={studentClasses}
                    classTypes={classTypes}
                    certificates={certificates}
                    timeLogs={timeLogs}
                    currentUser={currentUser}
                    consolidateTimeLogs={consolidateTimeLogs}
                    groupTimeLogsByGrade={groupTimeLogsByGrade}
                  />
                </Suspense>
              )}
            </div>
          )}

          {currentUserRole === 'parent' && parentView === 'certificate-approval' && (
            <div className="post post-margin-bottom">
              <h2>Certificate Approval</h2>
              {!userProfile.linkedStudentUid ? (
                <p className="no-posts">No student linked to this account yet. Please log out and sign up again as a parent with your student's ID.</p>
              ) : (
                <div className="create-post approval-block">
                  <CertificateParentApprovalList
                    certificates={certificates}
                    linkedStudentUid={userProfile.linkedStudentUid}
                    noContainer
                  />
                </div>
              )}
            </div>
          )}

          {currentUserRole === 'parent' && parentView === 'activity-approval' && userProfile.linkedStudentUid && (
            <div className="post post-margin-bottom">
              <h2>{`My Student's Activity Log`}</h2>
              <p className="transcript-description">Approved activities grouped by grade and activity.</p>
              <Suspense fallback={<ComponentLoader />}>
                <TimeLogDisplay
                  timeLogs={timeLogs}
                  currentUser={currentUser}
                  expandedTimeLogs={expandedTimeLogs}
                  toggleTimeLogExpansion={toggleTimeLogExpansion}
                  handleToggleConsolidatedFeatured={handleToggleConsolidatedFeatured}
                  consolidateTimeLogs={consolidateTimeLogs}
                  groupTimeLogsByGrade={groupTimeLogsByGrade}
                  studentId={userProfile.linkedStudentUid}
                  handleDeleteConsolidated={handleDeleteConsolidated}
                  handleDeleteEntry={handleDeleteEntry}
                  hideHeader
                />
              </Suspense>
            </div>
          )}

          {currentUserRole === 'student' && studentView === 'mentors' && (
            <Suspense fallback={<ComponentLoader />}>
              <MentorDisplay
                mentors={mentors}
                isTeacher={false}
                currentUser={currentUser}
                userProfile={userProfile}
                mentorRequests={mentorRequests}
              />
            </Suspense>
          )}

          {currentUserRole === 'teacher' && teacherViewMode === 'student-feedback' && selectedStudentForFeedback && (
            <>
              {/* Current Projects Management */}
              <div className="post post-margin-bottom">
                <h2>{selectedStudentForFeedback.firstName || selectedStudentForFeedback.name || selectedStudentForFeedback.id}'s Current Projects</h2>
                <p className="transcript-description">
                  View the student's active project management boards.
                </p>
                <Suspense fallback={<ComponentLoader />}>
                  <TeacherProjectManagementViewLazy
                    currentUser={currentUser}
                    selectedStudentUid={selectedStudentForFeedback.id}
                    studentName={selectedStudentForFeedback.firstName || selectedStudentForFeedback.name || selectedStudentForFeedback.id}
                    userProfile={userProfile}
                  />
                </Suspense>
              </div>

              <div className="post post-margin-bottom">
                <h2>{selectedStudentForFeedback.firstName || selectedStudentForFeedback.name || selectedStudentForFeedback.id}'s Competency Summary</h2>

                <CompetencySummary
                  studentId={selectedStudentForFeedback.id}
                  tagFeedbacks={tagFeedbacks}
                  posts={posts}
                  expandedTags={expandedTags}
                  toggleTagExpansion={toggleTagExpansion}
                  navigateToPost={navigateToPostWrapper}
                  noFeedbackMessage="No feedback yet for this student."
                />
                  
                  {/* Competencies Summary */}
                  <SkillAveragesGrid
                    studentId={selectedStudentForFeedback.id}
                    tags={tags}
                    tagFeedbacks={tagFeedbacks}
                  />
              </div>
            </>
          )}

          {currentUserRole === 'teacher' && teacherViewMode === 'transcripts' && selectedStudentUid !== 'all' && (
            <div className="post post-margin-bottom">
              <h2>{getStudentDisplayName(selectedStudentUid)}'s Transcript</h2>
              <p className="transcript-description">
                Overview of all classes, projects, and competencies.
              </p>
              
              {/* PDF Download Button */}
              <div className="pdf-download-container">
                <button 
                  onClick={handleDownloadTranscriptPDF}
                  className="pdf-download-button"
                >
                  Download PDF Transcript
                </button>
              </div>

              <Suspense fallback={<ComponentLoader />}>
                <TranscriptProjectsSection
                  studentId={selectedStudentUid}
                  posts={posts}
                  tagFeedbacks={tagFeedbacks}
                  tags={tags}
                  studentClasses={studentClasses}
                  classTypes={classTypes}
                  certificates={certificates}
                  timeLogs={timeLogs}
                  currentUser={currentUser}
                  graduationRequirements={graduationRequirements}
                  requirementTypes={GRADUATION_REQUIREMENT_TYPES}
                  consolidateTimeLogs={consolidateTimeLogs}
                  groupTimeLogsByGrade={groupTimeLogsByGrade}
                  showGraduationRequirements={true}
                />
              </Suspense>
            </div>
          )}

          {currentUserRole === 'teacher' && teacherViewMode === 'activity-logging' && selectedStudentUid !== 'all' && (
            <div className="post post-margin-bottom">
              <h2>{getStudentDisplayName(selectedStudentUid)}'s Activity Log</h2>
              <p className="transcript-description">
                View all logged activities by the selected student. Entries are grouped and expandable.
              </p>
              <Suspense fallback={<ComponentLoader />}>
                <TimeLogDisplay
                  timeLogs={timeLogs}
                  currentUser={currentUser}
                  expandedTimeLogs={expandedTimeLogs}
                  toggleTimeLogExpansion={toggleTimeLogExpansion}
                  handleToggleConsolidatedFeatured={handleToggleConsolidatedFeatured}
                  consolidateTimeLogs={consolidateTimeLogs}
                  groupTimeLogsByGrade={groupTimeLogsByGrade}
                  studentId={selectedStudentUid}
                />
              </Suspense>
            </div>
          )}

          {currentUserRole === 'teacher' && teacherViewMode === 'certificates-view' && selectedStudentUid !== 'all' && (
            <div className="post post-margin-bottom">
              <h2>{getStudentDisplayName(selectedStudentUid)}'s Certificates & Awards</h2>
              <p className="transcript-description">
                View all certificates and awards by the selected student.
              </p>
              <Suspense fallback={<ComponentLoader />}>
                <CertificateTeacherDisplay
                  certificates={certificates}
                  currentUser={currentUser}
                  studentId={selectedStudentUid}
                />
              </Suspense>
            </div>
          )}

          {currentUserRole === 'teacher' && teacherViewMode === 'class-management' && (
            <div className="post post-margin-bottom">
              <h2>Class Management</h2>
              <p className="transcript-description">
                Manage class proposals, assign classes to students, or students to classes.
              </p>
              
              {/* Class Proposal Management */}
              <Suspense fallback={<ComponentLoader />}>
                <ClassProposalManagement
                  classProposals={classProposals.filter(p => p.status === 'pending')}
                  classTypes={classTypes}
                  editingProposal={editingProposal}
                  editProposalForm={editProposalForm}
                  setEditProposalForm={setEditProposalForm}
                  handleApproveProposal={handleApproveProposal}
                  handleRejectProposal={handleRejectProposal}
                  handleEditProposal={handleEditProposal}
                  handleSaveProposalEdit={handleSaveProposalEdit}
                  handleCancelProposalEdit={handleCancelProposalEdit}
                />
              </Suspense>

              {/* Student-Focused Workflow */}
              <Suspense fallback={<ComponentLoader />}>
                <StudentFocusedAssignment
                  students={students}
                  studentClasses={studentClasses}
                  classTypes={classTypes}
                  selectedStudentId={selectedStudentId}
                  setSelectedStudentId={setSelectedStudentId}
                  studentAssignmentClasses={studentAssignmentClasses}
                  setStudentAssignmentClasses={setStudentAssignmentClasses}
                  handleAssignClassesToStudent={handleAssignClassesToStudent}
                  handleRemoveClassFromStudent={handleRemoveClassFromStudent}
                />
              </Suspense>

              {/* Class-Focused Workflow */}
              <Suspense fallback={<ComponentLoader />}>
                <ClassFocusedAssignment
                  students={students}
                  studentClasses={studentClasses}
                  classTypes={classTypes}
                  selectedClassId={selectedClassId}
                  setSelectedClassId={setSelectedClassId}
                  classAssignmentStudents={classAssignmentStudents}
                  setClassAssignmentStudents={setClassAssignmentStudents}
                  handleAssignStudentsToClass={handleAssignStudentsToClass}
                  handleRemoveClassFromStudent={handleRemoveClassFromStudent}
                />
              </Suspense>

              {/* Class Editing Section */}
              <Suspense fallback={<ComponentLoader />}>
                <ClassEditingSection
                  studentClasses={studentClasses}
                  classTypes={classTypes}
                  selectedEditClassName={selectedEditClassName}
                  setSelectedEditClassName={setSelectedEditClassName}
                  editClassForm={editClassForm}
                  setEditClassForm={setEditClassForm}
                  handleUpdateClass={handleUpdateClass}
                />
              </Suspense>

              {/* Flexible Class Assignment */}
              <Suspense fallback={<ComponentLoader />}>
                <FlexibleClassAssignmentForm
                  newClass={newClass}
                  setNewClass={setNewClass}
                  students={students}
                  classTypes={classTypes}
                  handleAddFlexibleClass={handleAddFlexibleClass}
                />
              </Suspense>

              {/* Delete Class Entirely */}
              <Suspense fallback={<ComponentLoader />}>
                <DeleteClassEntirely
                  studentClasses={studentClasses}
                  classTypes={classTypes}
                  selectedClassForDeletion={selectedClassForDeletion}
                  setSelectedClassForDeletion={setSelectedClassForDeletion}
                  handleDeleteClassEntirely={handleDeleteClassEntirely}
                />
              </Suspense>

            </div>
          )}

          {currentUserRole === 'teacher' && teacherViewMode === 'graduation-requirements' && (
            <div className="post post-margin-bottom">
              <h2>Graduation Requirements Certification</h2>
              <p className="transcript-description">
                Certify that students have met graduation requirements. Select a student to mark their completed requirements.
              </p>
              
              <Suspense fallback={<ComponentLoader />}>
                <GraduationRequirementsDisplay
                  students={students}
                  graduationRequirements={graduationRequirements}
                  loading={graduationRequirementsLoading}
                  addGraduationRequirement={addGraduationRequirement}
                  removeGraduationRequirement={removeGraduationRequirement}
                  selectedStudentUid={selectedStudentUid}
                  setSelectedStudentUid={setSelectedStudentUid}
                />
              </Suspense>
            </div>
          )}

          {/* Teacher Book List Management */}
          {currentUserRole === 'teacher' && teacherViewMode === 'book-list-management' && (
            <Suspense fallback={<ComponentLoader />}>
              <BookListManagementViewLazy
                bookLists={bookLists}
                newBookList={newBookList}
                setNewBookList={setNewBookList}
                handleAddBookList={handleAddBookList}
                editingBookListId={editingBookListId}
                editBookListForm={editBookListForm}
                setEditBookListForm={setEditBookListForm}
                handleStartEditBookList={handleStartEditBookList}
                handleSaveBookListEdit={handleSaveBookListEdit}
                handleCancelEditBookList={handleCancelEditBookList}
                handleDeleteBookList={handleDeleteBookList}
                newBookEntry={newBookEntry}
                setNewBookEntry={setNewBookEntry}
                handleAddBookToList={handleAddBookToList}
                handleRemoveBookFromList={handleRemoveBookFromList}
                handleAddBookToEditList={handleAddBookToEditList}
                handleRemoveBookFromEditList={handleRemoveBookFromEditList}
              />
            </Suspense>
          )}

          {/* Teacher Book List Progress Overview */}
          {currentUserRole === 'teacher' && teacherViewMode === 'book-list-progress' && (
            <Suspense fallback={<ComponentLoader />}>
              <BookListProgressOverviewLazy
                bookLists={bookLists}
                bookLogs={bookLogs}
                students={students}
              />
            </Suspense>
          )}

          {/* Teacher Word of the Day Management */}
          {currentUserRole === 'teacher' && teacherViewMode === 'word-of-day-management' && (
            <Suspense fallback={<ComponentLoader />}>
              <WordOfDayManagementViewLazy
                words={words}
                newWord={newWord}
                setNewWord={setNewWord}
                handleAddWord={handleAddWord}
                editingWordId={editingWordId}
                editWordForm={editWordForm}
                setEditWordForm={setEditWordForm}
                handleStartEditWord={handleStartEditWord}
                handleSaveWordEdit={handleSaveWordEdit}
                handleCancelEditWord={handleCancelEditWord}
                handleDeleteWord={handleDeleteWord}
                quotes={quotes}
                newQuote={newQuote}
                setNewQuote={setNewQuote}
                handleAddQuote={handleAddQuote}
                editingQuoteId={editingQuoteId}
                editQuoteForm={editQuoteForm}
                setEditQuoteForm={setEditQuoteForm}
                handleStartEditQuote={handleStartEditQuote}
                handleSaveQuoteEdit={handleSaveQuoteEdit}
                handleCancelEditQuote={handleCancelEditQuote}
                handleDeleteQuote={handleDeleteQuote}
              />
            </Suspense>
          )}

          {/* Teacher Resource Management */}
          {currentUserRole === 'teacher' && teacherViewMode === 'resource-management' && (
            <Suspense fallback={<ComponentLoader />}>
              <ResourceManagementView
                resources={resources}
                resourceCategories={resourceCategories}
                newResource={newResource}
                setNewResource={setNewResource}
                editingResourceId={editingResourceId}
                editResourceForm={editResourceForm}
                setEditResourceForm={setEditResourceForm}
                newCategory={newCategory}
                setNewCategory={setNewCategory}
                editingCategoryId={editingCategoryId}
                editCategoryName={editCategoryName}
                setEditCategoryName={setEditCategoryName}
                handleAddCategory={handleAddCategory}
                handleEditCategory={handleEditCategory}
                handleSaveCategory={handleSaveCategory}
                handleCancelEditCategory={handleCancelEditCategory}
                handleDeleteCategory={handleDeleteCategory}
                handleMoveCategoryUp={handleMoveCategoryUp}
                handleMoveCategoryDown={handleMoveCategoryDown}
                handleAddResource={handleAddResource}
                handleEditResource={handleEditResource}
                handleSaveResource={handleSaveResource}
                handleCancelEditResource={handleCancelEditResource}
                handleDeleteResource={handleDeleteResource}
                handleMoveResourceUp={handleMoveResourceUp}
                handleMoveResourceDown={handleMoveResourceDown}
              />
            </Suspense>
          )}

          {currentUserRole === 'teacher' && teacherViewMode === 'mentor-management' && (
            <>
              <Suspense fallback={<ComponentLoader />}>
                <MentorCreationForm
                  newMentor={newMentor}
                  setNewMentor={setNewMentor}
                />
              </Suspense>
              
              <Suspense fallback={<ComponentLoader />}>
                <MentorDisplay
                  mentors={mentors}
                  isTeacher={true}
                  currentUser={currentUser}
                  userProfile={userProfile}
                  mentorRequests={mentorRequests}
                />
              </Suspense>
            </>
          )}

          {/* Teacher Letter of Recommendation Management */}
          {currentUserRole === 'teacher' && teacherViewMode === 'lor-management' && (
            <>
              <Suspense fallback={<ComponentLoader />}>
                <LorManagementView
                  lorRequests={lorRequests}
                  handleCompleteLorRequest={handleCompleteLorRequest}
                  handleDeleteLorRequest={handleDeleteLorRequest}
                />
              </Suspense>
              
              <Suspense fallback={<ComponentLoader />}>
                <TeacherTranscriptRequestsDisplay
                  transcriptRequests={transcriptRequests}
                  onComplete={handleCompleteTranscriptRequest}
                  onDelete={handleDeleteTranscriptRequest}
                />
              </Suspense>
            </>
          )}

          {currentUserRole === 'teacher' && teacherViewMode === 'project-management' && (
            <Suspense fallback={<ComponentLoader />}>
              <ProjectProposalManagementViewLazy
                projectProposals={projectProposals}
                handleApproveProjectProposal={handleApproveProjectProposal}
                handleRejectProjectProposal={handleRejectProjectProposal}
                handleDeleteProjectProposal={handleDeleteProjectProposal}
                usersById={Object.fromEntries((allUsers || []).map(u => [u.id, u]))}
              />
            </Suspense>
          )}

          {((currentUserRole === 'student' && studentView === 'feed') || (currentUserRole === 'teacher' && teacherViewMode === 'student-feedback')) && (
          <div className="posts-container">
            <h2>
              {currentUserRole === 'teacher' && selectedStudentUid !== 'all'
                ? `Posts by ${getStudentDisplayName(selectedStudentUid)}`
                : selectedTag === 'all'
                  ? 'All Reflections'
                  : `Posts tagged: ${selectedTag}`}
            </h2>
            {(getFilteredPosts({ posts, currentUser, userProfile, selectedStudentUid, selectedTag }) || []).length === 0 ? (
              <p className="no-posts">No posts yet!</p>
            ) : (
              (() => {
                const filteredPosts = getFilteredPosts({ posts, currentUser, userProfile, selectedStudentUid, selectedTag });
                const postsByGrade = groupPostsByGrade(filteredPosts);
                
                return postsByGrade.map(gradeGroup => {
                  const grade = gradeGroup.grade;
                  const gradePosts = gradeGroup.posts;
                  const isExpanded = expandedGrades.has(grade);
                  const gradeLabel = grade === 'Unknown' ? 'Unknown Grade' : grade === 'Portfolio Defense' ? 'Portfolio Defense' : `${grade}th Grade`;
                  
                  return (
                    <div key={grade} className="grade-section">
                      <div 
                        className="grade-header"
                        onClick={() => toggleGradeExpansion(grade)}
                      >
                        <div className={`grade-arrow ${isExpanded ? 'expanded' : ''}`}>
                          ▶
                        </div>
                        <h3 className="grade-title">
                          {gradeLabel} ({gradePosts.length} posts)
                        </h3>
                      </div>
                      
                      {isExpanded && gradePosts.map(post => (
                        <Suspense key={post.id} fallback={<ComponentLoader />}>
                          <PostItem
                            post={post}
                            currentUser={currentUser}
                            currentUserRole={currentUserRole}
                            isPostGraded={isPostGradedWrapper}
                            getPostTagFeedbacks={getPostTagFeedbacksWrapper}
                            getPostComments={getPostCommentsWrapper}
                            newTagFeedback={newTagFeedback}
                            setNewTagFeedback={setNewTagFeedback}
                            newComment={newComment}
                            setNewComment={setNewComment}
                            teacherExtraFeedback={teacherExtraFeedback}
                            setTeacherExtraFeedback={setTeacherExtraFeedback}
                          tags={tags}
                            studentClasses={studentClasses}
                            classTypes={classTypes}
                            highlightedPostId={highlightedPostId}
                            editingPostId={editingPostId}
                            editPostForm={editPostForm}
                            setEditPostForm={setEditPostForm}
                            newTagJustificationEdit={newTagJustificationEdit}
                            setNewTagJustificationEdit={setNewTagJustificationEdit}
                            newLinkEdit={newLinkEdit}
                            setNewLinkEdit={setNewLinkEdit}
                            editingFeedbackId={editingFeedbackId}
                            editingFeedbackForm={editingFeedbackForm}
                            setEditingFeedbackId={setEditingFeedbackId}
                            setEditingFeedbackForm={setEditingFeedbackForm}
                            isExpanded={expandedPosts.has(post.id)}
                            onToggleExpansion={togglePostExpansion}
                            onToggleFeatured={handleToggleFeatured}
                            onToggleHideFromTranscript={handleToggleHideFromTranscript}
                            onEditPost={handleEditPost}
                            onDeletePost={handleDeletePost}
                            onSaveEditPost={handleSaveEditPostWrapper}
                            onCancelEditPost={handleCancelEditPost}
                            onAddTagJustificationEdit={handleAddTagJustificationEdit}
                            onRemoveTagJustificationEdit={handleRemoveTagJustificationEdit}
                            onAddLinkEdit={handleAddLinkEdit}
                            onRemoveLinkEdit={handleRemoveLinkEdit}
                            onAddTagFeedback={handleAddTagFeedbackWrapper}
                            onAddComment={handleAddCommentWrapper}
                            onEditFeedback={handleEditFeedback}
                            onSaveFeedback={handleSaveFeedback}
                            onCancelEditFeedback={handleCancelEditFeedback}
                            onDeleteFeedback={handleDeleteFeedbackWrapper}
                          />
                        </Suspense>
              ))}
                    </div>
                  );
                });
              })()
            )}
          </div>
          )}
        </main>
      </div>
    </div>
  );
  } catch (error) {
    console.error('Error rendering StudentPortfolioApp:', error);
    return (
      <div className="error-message">
        <h1>Error in StudentPortfolioApp:</h1>
        <p>{error.message}</p>
        <pre>{error.stack}</pre>
      </div>
    );
  }
};

export default App;
